import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import SearchBar from "../components/dashboard/SearchBar";
import DateFilter from "../components/dashboard/DateFilter";
import JobOrderTable, { ALL_COLUMNS } from "../components/dashboard/JobOrderTable";
import JobOrderDetailModal from "../components/modals/JobOrderDetailModal";
import ChatModal from "../components/modals/ChatModal";
import RescheduleModal from "../components/modals/RescheduleModal";
import { QuickCompleteDialog, QuickCancelDialog } from "../components/modals/QuickNIStatusDialogs";
import { QuickCallStatusDialog, NI_CALL_OPTIONS } from "../components/modals/QuickCallStatusDialog";
import StatusActionMenu from "../components/StatusActionMenu";
import CallStatusActionMenu from "../components/CallStatusActionMenu";
import TeamAssignmentPopover from "../components/common/TeamAssignmentPopover";
import CompanyAssignmentPopover from "../components/common/CompanyAssignmentPopover";
import useChatNotifications from "../hooks/useChatNotifications";
import { getLocalToday, calculatePendingAgeing } from "../utils/helpers";
import { MODULE_THEMES } from "../utils/moduleTheme";

const NI = MODULE_THEMES.ni;
const PREF_KEY = "ni_dashboard_view";

/** Columns that are hidden by default (user can unhide them). */
const DEFAULT_HIDDEN = new Set(["reference_id", "municipality", "complete_address", "serial_number", "nap_id"]);

export default function DashboardPage() {
  const { user, canViewDetail, hasPerm, isSalesAgent } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const canViewChat = hasPerm("chat.view");
  const canExportNI = hasPerm("ni.export");
  const canAccessNiDashboard = hasPerm("page.ni_dashboard");
  const canEditTerminalTeam = hasPerm("jo.edit_terminal_team");
  const canEditCrossCompanyTeam = hasPerm("jo.team_cross_company");

  // Filters & pagination state.
  //
  // NOTE on page_size = 10000: We mirror Migration page's loading strategy —
  // a single request fetches the entire working set for the active date
  // range / filters, and all subsequent UX (sorting, filter dropdowns,
  // search, stat-card filtering) happens client-side. This trades a slightly
  // larger initial payload for instant interactions afterwards and removes
  // the infinite-scroll race conditions that previously caused the table to
  // "reset to 50 rescheduled rows" at the tail of long ranges. The backend
  // endpoint accepts up to 10000 rows in a single page; in practice typical
  // working sets are well under 1000 rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10000);
  const [search, setSearch] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => {
    const s = location.state;
    if (s?.dateFrom && s?.dateTo) return { from: s.dateFrom, to: s.dateTo };
    return { from: getLocalToday(), to: getLocalToday() };
  });
  const [sortStack, setSortStack] = useState([{ key: "updated_at", dir: "desc" }]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [pendingTeamFilter, setPendingTeamFilter] = useState(null);
  const [acSubFilter, setAcSubFilter] = useState(null);
  const [acCompanyFilter, setAcCompanyFilter] = useState(null);
  const [cancelReasonFilter, setCancelReasonFilter] = useState(null);
  const [reschedReasonFilter, setReschedReasonFilter] = useState(null);
  const [teamFilter, setTeamFilter] = useState("");
  const [tlFilter, setTlFilter] = useState("");

  // Column visibility (desktop only)
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set(DEFAULT_HIDDEN));
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const colMenuRef = useRef(null);

  // Preference loading flag
  const [prefLoaded, setPrefLoaded] = useState(false);
  const savePrefRef = useRef(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Backward-compat: derive primary sort for the API
  const sortBy = sortStack.length > 0 ? sortStack[0].key : "job_date";
  const sortDir = sortStack.length > 0 ? sortStack[0].dir : "desc";

  // Data state — accumulated items for infinite scroll
  const [allItems, setAllItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    Pending: 0,
    "Active/Complete": 0,
    Rescheduled: 0,
    Cancelled: 0,
  });
  const [totalStatusCounts, setTotalStatusCounts] = useState({
    Pending: 0,
    "Active/Complete": 0,
    Rescheduled: 0,
    Cancelled: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayPage, setDisplayPage] = useState(1);
  const [teamsItems, setTeamsItems] = useState([]);

  // Modal state
  const [selectedRow, setSelectedRow] = useState(null);
  const [chatJobOrderId, setChatJobOrderId] = useState(null);

  // Quick status-action menu state
  const [statusMenu, setStatusMenu] = useState(null);   // { jo, rect }
  const [directAction, setDirectAction] = useState(null); // { type: "complete"|"cancel"|"reschedule", jo }

  const statusMenuActions = useMemo(() => {
    const a = [];
    if (hasPerm("jo.complete")) a.push("complete");
    if (hasPerm("jo.cancel")) a.push("cancel");
    if (hasPerm("jo.reschedule")) a.push("reschedule");
    return a;
  }, [hasPerm]);

  const handleStatusClick = useCallback((jo, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setStatusMenu({ jo, rect });
  }, []);

  const handleStatusAction = useCallback((action) => {
    if (!statusMenu) return;
    setDirectAction({ type: action, jo: statusMenu.jo });
    setStatusMenu(null);
  }, [statusMenu]);

  const handleDirectActionDone = useCallback(() => {
    setDirectAction(null);
    handleRefresh();
  }, []);  // handleRefresh is stable

  // Quick call-status action state
  const [csMenu, setCsMenu] = useState(null);            // { jo, rect }
  const [csAction, setCsAction] = useState(null);         // { jo, selectedStatus }
  const canEditCallStatus = hasPerm("field.edit.call_status");

  // Inline team assignment popover state
  const [teamMenu, setTeamMenu] = useState(null);         // { jo, rect }
  const canEditTeam = hasPerm("field.edit.team_number");

  const handleTeamClick = useCallback((jo, e) => {
    // Cross-company permission holders skip the company locks entirely.
    if (!canEditCrossCompanyTeam) {
      // Block team edit if the team was first assigned by another company...
      if (jo.team_assigned_by_company && jo.team_assigned_by_company !== user?.company_id) return;
      // ...or if the JO has an assigned Company the current user does not belong to.
      if (jo.company_id && user?.company_id && jo.company_id !== user.company_id) return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setTeamMenu({ jo, rect });
  }, [user?.company_id, canEditCrossCompanyTeam]);

  const handleTeamSave = useCallback((updated) => {
    setTeamMenu(null);
    // Quiet, in-place save (no full refresh) so the page does not flash, scroll
    // to top, or reset the date filter — preserving the user's momentum while
    // assigning teams, the same way the company assignment saves silently. The
    // backend returns the full updated JobOrderRead; merge it into the matching
    // row (history rows match by history_id). `updated_at` is kept so the
    // default `updated_at desc` sort does not bump the edited row. Bulk
    // composition changes ("Update All Records") on sibling rows only appear
    // after the next manual refresh.
    if (!updated) return;
    setAllItems((prev) =>
      prev.map((item) => {
        const match = updated.is_history
          ? item.is_history && item.history_id === updated.history_id
          : !item.is_history && item.id === updated.id;
        return match ? { ...item, ...updated, updated_at: item.updated_at } : item;
      })
    );
  }, []);

  // Inline company assignment popover state
  const [companyMenu, setCompanyMenu] = useState(null);   // { jo, rect }
  const canEditCompany = hasPerm("field.edit.company");

  const handleCompanyClick = useCallback((jo, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCompanyMenu({ jo, rect });
  }, []);

  const handleCompanySave = useCallback((updated) => {
    setCompanyMenu(null);
    // Quiet, in-place save (no full refresh) so the page does not flash, scroll
    // to top, or reset the date filter. The backend returns the full updated
    // JobOrderRead, so merging it into the row also refreshes that row's
    // team-number lock fields (driven by the assigned company).
    if (!updated) return;
    setAllItems((prev) =>
      prev.map((item) =>
        item.id === updated.id && !item.is_history
          // Keep the row's existing `updated_at` so the default `updated_at desc`
          // sort does not bump the edited row to the top — a true quiet save.
          ? { ...item, ...updated, updated_at: item.updated_at }
          : item
      )
    );
  }, []);

  const handleCallStatusClick = useCallback((jo, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCsMenu({ jo, rect });
  }, []);

  const handleCallStatusSelect = useCallback((status) => {
    if (!csMenu) return;
    setCsAction({ jo: csMenu.jo, selectedStatus: status });
    setCsMenu(null);
  }, [csMenu]);

  const handleCallStatusDone = useCallback(() => {
    setCsAction(null);
    handleRefresh();
  }, []);

  // Real-time chat notifications via SSE
  const chatNotifRef = useRef(null);  // latest SSE event for open ChatModal
  useChatNotifications(canViewChat, useCallback((ev) => {
    // Update the table row's chat indicator
    setAllItems((prev) =>
      prev.map((item) =>
        item.id === ev.job_order_id && !item.is_history
          ? { ...item, chat_count: ev.chat_count, has_unread_chat: true, unread_count: (item.unread_count || 0) + 1 }
          : item
      )
    );
    // Store latest event so open ChatModal can pick it up
    chatNotifRef.current = ev;
  }, []));

  // Scroll ref for infinite scroll
  const scrollRef = useRef(null);

  // AbortController ref to cancel stale requests on date/filter change
  const abortRef = useRef(null);

  // Preserve horizontal scroll position across sort/refetch cycles
  const savedScrollLeft = useRef(0);

  // Track whether a filter/sort change happened (reset) vs. a "load more"
  const isLoadMore = useRef(false);

  // Load saved user preferences on mount.
  //
  // NOTE: We deliberately do NOT restore `sortStack` from saved preferences.
  // The product requirement is that the NI dashboard always opens with the
  // default sort (latest updated record on top) after every browser refresh
  // or page navigation. Restoring a saved sortStack would make a previously
  // chosen header sort persist across sessions, defeating that requirement.
  // Per-session header clicks still work and are preserved while the user
  // stays on the page; they are simply not saved to the server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/preferences/${PREF_KEY}`);
        if (cancelled || !data.value) { setPrefLoaded(true); return; }
        const pref = JSON.parse(data.value);
        if (pref.hiddenColumns) setHiddenColumns(new Set(pref.hiddenColumns));
        if (pref.teamFilter) setTeamFilter(pref.teamFilter);
        if (pref.tlFilter) setTlFilter(pref.tlFilter);
      } catch { /* ignore — use defaults */ }
      if (!cancelled) setPrefLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Close column menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColumnMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchJobOrders = useCallback(async () => {
    // Cancel any in-flight request to prevent stale responses from overwriting fresh data
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // CRITICAL: Snapshot the load-more flag at the START of this fetch into a
    // local variable. Do NOT read isLoadMore.current later in this function —
    // the previous fetch's `finally` block (running asynchronously after we
    // call `abort()` above) will reset the ref to false BEFORE this fetch's
    // response arrives. Reading the ref again would mis-classify a load-more
    // response as a fresh load and cause `setAllItems(data.items)` to wipe
    // the accumulated list back down to one page (the "auto-refresh to all
    // rescheduled, capped at 100" bug observed during long-range scrolling).
    const isLoadMoreLocal = isLoadMore.current;

    if (isLoadMoreLocal) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (dateFilter?.from) params.job_date_from = dateFilter.from;
      if (dateFilter?.to) params.job_date_to = dateFilter.to;
      if (statusFilter) params.jo_status = statusFilter;
      if (pendingTeamFilter === "with_team") params.has_team = true;
      else if (pendingTeamFilter === "without_team") params.has_team = false;
      if (acSubFilter) params.ac_sub = acSubFilter;
      if (acCompanyFilter) params.ac_company = acCompanyFilter;
      if (cancelReasonFilter) params.cancellation_reason = cancelReasonFilter;
      if (reschedReasonFilter) params.reschedule_reason = reschedReasonFilter;
      const { data } = await api.get("/job-orders", { params, signal: controller.signal });

      if (isLoadMoreLocal) {
        // Append new items, deduplicating by unique row identity
        setAllItems((prev) => {
          const seen = new Set(prev.map(r => r.is_history ? `h-${r.history_id}` : `a-${r.id}`));
          const fresh = data.items.filter(r => !seen.has(r.is_history ? `h-${r.history_id}` : `a-${r.id}`));
          return [...prev, ...fresh];
        });
      } else {
        // Fresh load — replace items and scroll to top
        setAllItems(data.items);
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
          // Restore horizontal scroll position saved before sort refetch
          if (savedScrollLeft.current > 0) {
            const saved = savedScrollLeft.current;
            savedScrollLeft.current = 0;
            requestAnimationFrame(() => {
              if (scrollRef.current) scrollRef.current.scrollLeft = saved;
            });
          }
        }
      }
      setTotal(data.total);
      setStatusCounts({
        Pending: data.status_counts?.Pending || 0,
        "Active/Complete": data.status_counts?.["Active/Complete"] || 0,
        Rescheduled: data.status_counts?.Rescheduled || 0,
        Cancelled: data.status_counts?.Cancelled || 0,
      });

      // Fetch unfiltered status counts for the date range — fire-and-forget so
      // the table can render as soon as the primary fetch resolves. Previously
      // this was awaited inline, which delayed `setLoading(false)` until the
      // secondary request finished. The signal is shared so a stale response
      // is still discarded if the user changes filters mid-flight.
      (async () => {
        try {
          const unfileredParams = { page: 1, page_size: 1 };
          if (dateFilter?.from) unfileredParams.job_date_from = dateFilter.from;
          if (dateFilter?.to) unfileredParams.job_date_to = dateFilter.to;
          const { data: unfileredData } = await api.get("/job-orders", { params: unfileredParams, signal: controller.signal });
          setTotalStatusCounts({
            Pending: unfileredData.status_counts?.Pending || 0,
            "Active/Complete": unfileredData.status_counts?.["Active/Complete"] || 0,
            Rescheduled: unfileredData.status_counts?.Rescheduled || 0,
            Cancelled: unfileredData.status_counts?.Cancelled || 0,
            pending_with_team: unfileredData.status_counts?.pending_with_team || 0,
            pending_without_team: unfileredData.status_counts?.pending_without_team || 0,
            ac_gofiber_only: unfileredData.status_counts?.ac_gofiber_only || 0,
            ac_jo_only: unfileredData.status_counts?.ac_jo_only || 0,
            ac_both: unfileredData.status_counts?.ac_both || 0,
            ac_company_best: unfileredData.status_counts?.ac_company_best || 0,
            ac_company_a6: unfileredData.status_counts?.ac_company_a6 || 0,
            ac_company_unknown: unfileredData.status_counts?.ac_company_unknown || 0,
            cancel_reason_counts: unfileredData.status_counts?.cancel_reason_counts || {},
            resched_reason_counts: unfileredData.status_counts?.resched_reason_counts || {},
          });
        } catch {
          // ignore — secondary counts are best-effort and must not clear the
          // primary data on failure or cancellation.
        }
      })();
    } catch (err) {
      // Ignore aborted requests — a newer request has already taken over.
      // CRITICAL: return BEFORE the finally clears the ref, so the newer
      // (still-in-flight) fetch can keep using its own snapshot.
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      if (!isLoadMoreLocal) {
        setAllItems([]);
        setTotal(0);
        setStatusCounts({ Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 });
        setTotalStatusCounts({ Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 });
      }
    } finally {
      // Only flip global loading flags if this fetch is still the active one.
      // If we were aborted, a newer fetch owns these flags now — don't stomp.
      if (abortRef.current === controller) {
        setLoading(false);
        setLoadingMore(false);
        // Reset the load-more flag only when the active fetch settles.
        // Aborted fetches must NEVER reset it, otherwise the newer fetch's
        // response handler would mis-classify itself as a fresh load.
        isLoadMore.current = false;
      }
    }
  }, [page, pageSize, search, dateFilter, sortBy, sortDir, statusFilter, pendingTeamFilter, acSubFilter, acCompanyFilter, cancelReasonFilter, reschedReasonFilter]);

  useEffect(() => {
    fetchJobOrders();
  }, [fetchJobOrders]);

  const handleSearch = useCallback((value) => {
    isLoadMore.current = false;
    setSearch(value);
    setPage(1);
    setDisplayPage(1);
    setAllItems([]);
    if (value) {
      setStatusFilter(null);
      setPendingTeamFilter(null);
      setAcSubFilter(null);
      setAcCompanyFilter(null);
      setCancelReasonFilter(null);
      setReschedReasonFilter(null);
    }
  }, []);

  const handleDateChange = (value) => {
    isLoadMore.current = false;
    setDateFilter(value);
    setPage(1);
    setDisplayPage(1);
    setAllItems([]);
    setStatusFilter(null);
    setPendingTeamFilter(null);
    setAcSubFilter(null);
    setAcCompanyFilter(null);
    setCancelReasonFilter(null);
    setReschedReasonFilter(null);
  };

  const handleSort = (col, dir) => {
    isLoadMore.current = false;
    setSortStack([{ key: col, dir }]);
    setPage(1);
    setDisplayPage(1);
    setAllItems([]);
  };

  // Multi-sort click handler: normal click = single sort; Shift+click = add/toggle layer
  const handleSortClick = useCallback((key, event) => {
    const isShift = event && event.shiftKey;

    if (isShift) {
      // Shift+click: add/toggle a secondary sort level — pure client-side re-sort, no refetch
      setSortStack((prev) => {
        const idx = prev.findIndex((s) => s.key === key);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { key, dir: updated[idx].dir === "asc" ? "desc" : "asc" };
          return updated;
        }
        return [...prev, { key, dir: "asc" }];
      });
      return;
    }

    // Normal click: replace entire sort stack — triggers refetch via sortBy/sortDir change
    if (scrollRef.current) savedScrollLeft.current = scrollRef.current.scrollLeft;
    isLoadMore.current = false;
    setPage(1);
    setDisplayPage(1);
    setSortStack((prev) => {
      if (prev.length === 1 && prev[0].key === key) {
        return [{ key, dir: prev[0].dir === "asc" ? "desc" : "asc" }];
      }
      return [{ key, dir: "asc" }];
    });
  }, []);

  const handlePageSizeChange = (size) => {
    isLoadMore.current = false;
    setPageSize(size);
    setPage(1);
    setDisplayPage(1);
    setAllItems([]);
  };

  const handleRefresh = () => {
    isLoadMore.current = false;
    setPage(1);
    setDisplayPage(1);
    setAllItems([]);
    fetchJobOrders();
  };

  // Infinite scroll: load next page when user scrolls near the bottom
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasMore = page < totalPages;

  const handleTableScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // Derive visible page from scroll position using row height estimate
    const tbody = scrollRef.current.querySelector("tbody");
    if (tbody && tbody.children.length > 0) {
      const totalRows = tbody.children.length;
      const tbodyHeight = tbody.scrollHeight;
      const rowHeight = tbodyHeight / totalRows;
      if (rowHeight > 0) {
        const topRowIndex = Math.floor(scrollTop / rowHeight);
        const newDisplayPage = Math.min(
          Math.floor(topRowIndex / pageSize) + 1,
          totalPages
        );
        setDisplayPage((prev) => (prev !== newDisplayPage ? newDisplayPage : prev));
      }
    }

    // Load more data when near bottom
    if (!loadingMore && hasMore && scrollHeight - scrollTop - clientHeight < 150) {
      isLoadMore.current = true;
      setPage((prev) => prev + 1);
    }
  }, [loadingMore, hasMore, pageSize, totalPages]);

  // After items load, auto-trigger next page if still near the bottom
  useEffect(() => {
    if (loading || loadingMore || !hasMore || allItems.length === 0) return;
    const raf = requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollHeight - scrollTop - clientHeight < 150) {
        isLoadMore.current = true;
        setPage((prev) => prev + 1);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [loading, loadingMore, hasMore, allItems.length]);

  // Client-side multi-level sort
  //
  // KEY DESIGN: The NI table now loads its full date-range working set in a
  // single request (see pageSize note above), so client-side sorting is the
  // sole source of row order. We therefore always run the comparator when
  // sortStack is non-empty — even for a single sort layer — so the default
  // sort (`updated_at desc`, latest update on top) is honored regardless of
  // the backend's `sort_by` parameter, and Shift+click multi-sort still works.
  //
  // The comparator includes a deterministic identity tiebreaker so the
  // result is stable across re-renders and SSE-driven row updates.
  const sortedItems = useMemo(() => {
    if (!allItems.length) return allItems;
    if (!sortStack.length) return allItems; // no active sort — trust backend order

    const today = getLocalToday();
    const sorted = [...allItems];
    sorted.sort((a, b) => {
      for (const { key, dir } of sortStack) {
        // Company sorts by the displayed shorthand (prefix, falling back to name)
        let valA = key === "company_name" ? (a.company_prefix || a.company_name) : a[key];
        let valB = key === "company_name" ? (b.company_prefix || b.company_name) : b[key];
        const aEmpty = valA == null || valA === "";
        const bEmpty = valB == null || valB === "";
        if (aEmpty && bEmpty) continue;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (typeof valA === "number" && typeof valB === "number") {
          const cmp = dir === "asc" ? valA - valB : valB - valA;
          if (cmp !== 0) return cmp;
          continue;
        }
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
        if (valA < valB) return dir === "asc" ? -1 : 1;
        if (valA > valB) return dir === "asc" ? 1 : -1;
        // Secondary sort: when jo_status is the same "Pending", sort by ageing number
        if (key === "jo_status" && a.jo_status === "Pending" && b.jo_status === "Pending") {
          const ageA = calculatePendingAgeing(a, today) ?? 0;
          const ageB = calculatePendingAgeing(b, today) ?? 0;
          const ageCmp = dir === "asc" ? ageA - ageB : ageB - ageA;
          if (ageCmp !== 0) return ageCmp;
        }
      }
      // Final deterministic tiebreaker: keep active rows above history,
      // then sort by id matching the primary sort direction.
      if (a.is_history !== b.is_history) return a.is_history ? 1 : -1;
      const primaryDir = sortStack[0].dir;
      const idA = a.is_history ? (a.history_id ?? 0) : (a.id ?? 0);
      const idB = b.is_history ? (b.history_id ?? 0) : (b.id ?? 0);
      if (idA !== idB) {
        return primaryDir === "asc" ? idA - idB : idB - idA;
      }
      return 0;
    });
    return sorted;
  }, [allItems, sortStack]);

  // Fetch date-range-only items for TEAMS calculation (unaffected by status/search)
  useEffect(() => {
    let cancelled = false;
    const fetchTeamsData = async () => {
      try {
        const baseParams = { page_size: 100 };
        if (dateFilter?.from) baseParams.job_date_from = dateFilter.from;
        if (dateFilter?.to) baseParams.job_date_to = dateFilter.to;

        const { data: first } = await api.get("/job-orders", { params: { ...baseParams, page: 1 } });
        if (cancelled) return;

        let collected = first.items || [];
        const pages = Math.ceil((first.total || 0) / 100);
        for (let p = 2; p <= pages; p++) {
          const { data: next } = await api.get("/job-orders", { params: { ...baseParams, page: p } });
          if (cancelled) return;
          collected = collected.concat(next.items || []);
        }
        setTeamsItems(collected);
      } catch {
        if (!cancelled) setTeamsItems([]);
      }
    };
    fetchTeamsData();
    return () => { cancelled = true; };
  }, [dateFilter]);

  // Compute TEAMS count: unique team numbers per day, summed across days
  const teamsCount = useMemo(() => {
    const byDate = {};
    for (const item of teamsItems) {
      const raw = item.team_number;
      if (raw == null || raw === "") continue;
      const tn = String(raw).trim();
      if (tn === "") continue;
      const d = item.job_date || "";
      if (!byDate[d]) byDate[d] = new Set();
      byDate[d].add(tn);
    }
    let count = 0;
    for (const d in byDate) count += byDate[d].size;
    return count;
  }, [teamsItems]);

  // Team Number and TL filter option lists (derived from teamsItems = all JOs in date range)
  const teamOptions = useMemo(() => {
    const set = new Set();
    for (const item of teamsItems) {
      const v = item.team_number;
      if (v != null && String(v).trim()) set.add(String(v).trim());
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [teamsItems]);

  const tlOptions = useMemo(() => {
    const set = new Set();
    for (const item of teamsItems) {
      const v = item.assigned_tl_name;
      if (v != null && String(v).trim()) set.add(String(v).trim());
    }
    return [...set].sort();
  }, [teamsItems]);

  // Apply client-side Team + TL filters on already-sorted items
  const filteredItems = useMemo(() => {
    let list = sortedItems;
    if (teamFilter) {
      list = list.filter((r) => String(r.team_number || "").trim() === teamFilter);
    }
    if (tlFilter) {
      list = list.filter((r) => String(r.assigned_tl_name || "").trim() === tlFilter);
    }
    return list;
  }, [sortedItems, teamFilter, tlFilter]);

  // Toggle column visibility and auto-save preferences
  const toggleColumn = (key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);

      // Debounced auto-save — sortStack intentionally omitted (see
      // pref-loading effect above; default-sort must prevail on refresh).
      clearTimeout(savePrefRef.current);
      savePrefRef.current = setTimeout(() => {
        const pref = {
          hiddenColumns: [...next],
          teamFilter,
          tlFilter,
        };
        api.put(`/preferences/${PREF_KEY}`, { value: JSON.stringify(pref) }).catch(() => {});
      }, 500);

      return next;
    });
  };

  // Export NI to Excel
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      if (dateFilter?.from) params.job_date_from = dateFilter.from;
      if (dateFilter?.to) params.job_date_to = dateFilter.to;
      const defaultName = `NI_${dateFilter?.from || "all"}_to_${dateFilter?.to || "all"}.xlsx`;

      // Open the native "Save As" dialog BEFORE the network request. The File
      // System Access API requires transient user activation (~5s window);
      // calling it after `await api.get(...)` throws a silent SecurityError on
      // slow/large exports once activation expires. Acquire the handle first.
      let handle = null;
      if (window.showSaveFilePicker) {
        try {
          handle = await window.showSaveFilePicker({
            suggestedName: defaultName,
            types: [{
              description: "Excel Workbook",
              accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
            }],
          });
        } catch (pickerErr) {
          if (pickerErr.name === "AbortError") return; // user cancelled
          handle = null; // unsupported/blocked — fall back to anchor download
        }
      }

      const response = await api.get("/job-orders/export", {
        params,
        responseType: "blob",
      });

      if (handle) {
        const writable = await handle.createWritable();
        await writable.write(response.data);
        await writable.close();
      } else {
        // Fallback: auto-download
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = defaultName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      // silently fail — user sees button revert to normal
    } finally {
      setExporting(false);
    }
  }, [dateFilter, exporting]);

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      <Navbar />
      {/* Module accent strip */}
      <div className={`h-1 ${NI.strip} flex-shrink-0`} />

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-3 pb-2 w-full relative">
        {/* View toggle — anchored top-right for consistent positioning across Table/Map views */}
        {!isMobile && canAccessNiDashboard && (
          <div
            className="absolute top-3 right-4 lg:right-6 z-20 flex rounded-lg border border-gray-300 overflow-hidden cursor-pointer"
            onClick={() => navigate("/ni/dashboard", { state: { dateFrom: dateFilter.from, dateTo: dateFilter.to } })}
          >
            <span className={`px-3 py-1.5 text-sm font-medium ${NI.btnPrimaryStatic} flex items-center gap-1`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" /></svg>
              Table
            </span>
            <span className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
              Map
            </span>
          </div>
        )}

        {/* Toolbar */}
        <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 flex-shrink-0${canAccessNiDashboard ? " md:pr-[180px]" : ""}`}>
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <SearchBar onSearch={handleSearch} search={search} />
            <DateFilter value={dateFilter} onChange={handleDateChange} accent="teal" showViewAll={false} />
            {/* Team Number / TL dropdowns intentionally removed — the main
                search box now searches across all record fields (including
                team_number and assigned_tl_name), so these dropdown filters
                are redundant. State (teamFilter / tlFilter) is preserved so
                any saved preferences and downstream filtering logic still
                work without UI exposure. */}
          </div>
          <div className="flex gap-2 items-center justify-end flex-wrap">
            {/* Export to Excel — desktop only, permission-gated */}
            {!isMobile && canExportNI && (
              <button
                onClick={handleExport}
                disabled={exporting}
                className="border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center gap-1 disabled:opacity-50"
                title="Export NI to Excel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                {exporting ? "Exporting…" : "Export"}
              </button>
            )}
            {/* Column visibility toggle — desktop only */}
            {!isMobile && (
              <div className="relative" ref={colMenuRef}>
                <button
                  onClick={() => setShowColumnMenu((v) => !v)}
                  className="border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-1"
                  title="Show/Hide Columns"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 8h18M3 12h18M3 16h18M3 20h18" /></svg>
                  Columns
                </button>
                {showColumnMenu && (
                  <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 w-56 max-h-80 overflow-y-auto py-1">
                    {ALL_COLUMNS.filter((c) => c.hideable).map((col) => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="inline-flex items-center gap-2 rounded border border-teal-300 bg-teal-100 px-3 py-1.5 select-none whitespace-nowrap leading-none">
              <span className="text-xs font-semibold uppercase tracking-wide text-teal-800">Teams</span>
              <span className="text-sm font-bold text-teal-900 tabular-nums">{teamsCount}</span>
            </div>

          </div>
        </div>

        {/* Stats */}
        <div className="flex items-stretch gap-2 mb-3 flex-shrink-0">
          <div className={`flex-shrink-0 ${NI.badge} rounded-lg shadow-sm hidden sm:flex items-center gap-2 px-4 py-2`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={NI.iconPath} /></svg>
            <span className="text-xs font-bold uppercase tracking-wider">New Installation</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
          {/* Pending card — enhanced with team breakdown (desktop only) */}
          {(() => {
            const isPendingActive = statusFilter === "Pending" && !pendingTeamFilter;
            const isWithTeamActive = statusFilter === "Pending" && pendingTeamFilter === "with_team";
            const isWithoutTeamActive = statusFilter === "Pending" && pendingTeamFilter === "without_team";
            const pendingCount = totalStatusCounts.Pending || 0;
            const withTeam = totalStatusCounts.pending_with_team || 0;
            const withoutTeam = totalStatusCounts.pending_without_team || 0;
            const handlePendingClick = () => {
              isLoadMore.current = false;
              setSearch(null);
              setSortStack([{ key: "updated_at", dir: "desc" }]);
              setPage(1);
              setDisplayPage(1);
              setAllItems([]);
              setAcSubFilter(null);
              setAcCompanyFilter(null);
              setCancelReasonFilter(null);
              setReschedReasonFilter(null);
              if (isPendingActive) { setStatusFilter(null); setPendingTeamFilter(null); }
              else { setStatusFilter("Pending"); setPendingTeamFilter(null); }
            };
            const handleTeamClick = (type) => (e) => {
              e.stopPropagation();
              isLoadMore.current = false;
              setSearch(null);
              setSortStack([{ key: "updated_at", dir: "desc" }]);
              setPage(1);
              setDisplayPage(1);
              setAllItems([]);
              setAcSubFilter(null);
              setAcCompanyFilter(null);
              setCancelReasonFilter(null);
              setReschedReasonFilter(null);
              if (pendingTeamFilter === type) { setStatusFilter(null); setPendingTeamFilter(null); }
              else { setStatusFilter("Pending"); setPendingTeamFilter(type); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-yellow-100 text-yellow-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                  (isPendingActive || isWithTeamActive || isWithoutTeamActive) ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: pendingCount === 0 ? 0.5 : 1 }}
              >
                <div onClick={pendingCount > 0 ? handlePendingClick : undefined} tabIndex={0} title="Show only Pending job orders"
                  className={`cursor-pointer ${isPendingActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pending</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{pendingCount}</span>
                  </div>
                </div>
                {/* Desktop-only team breakdown */}
                <div className="hidden sm:flex items-center justify-center gap-1.5 mt-0.5 border-t border-yellow-300 pt-1">
                  <button onClick={handleTeamClick("with_team")} disabled={withTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${withTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending with team assigned"
                  >
                    <span className="font-semibold">{withTeam}</span> w/ Team
                  </button>
                  <span className="text-yellow-400 text-[10px]">|</span>
                  <button onClick={handleTeamClick("without_team")} disabled={withoutTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithoutTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${withoutTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending without team assigned"
                  >
                    <span className="font-semibold">{withoutTeam}</span> w/o Team
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Active/Complete card — enhanced with GoFiber/JO + Company breakdown (desktop only) */}
          {(() => {
            const isAcActive = statusFilter === "Active/Complete" && !acSubFilter && !acCompanyFilter;
            const isGfActive = statusFilter === "Active/Complete" && acSubFilter === "gofiber_only";
            const isJoActive = statusFilter === "Active/Complete" && acSubFilter === "jo_only";
            const isBothActive = statusFilter === "Active/Complete" && acSubFilter === "both";
            const isBestActive = statusFilter === "Active/Complete" && acCompanyFilter === "best";
            const isA6Active = statusFilter === "Active/Complete" && acCompanyFilter === "a6";
            const isUnkActive = statusFilter === "Active/Complete" && acCompanyFilter === "unknown";
            const anyAcSelected = statusFilter === "Active/Complete";
            const acCount = totalStatusCounts["Active/Complete"] || 0;
            const gfOnly = totalStatusCounts.ac_gofiber_only || 0;
            const joOnly = totalStatusCounts.ac_jo_only || 0;
            const both = totalStatusCounts.ac_both || 0;
            const bestCo = totalStatusCounts.ac_company_best || 0;
            const a6Co = totalStatusCounts.ac_company_a6 || 0;
            const unkCo = totalStatusCounts.ac_company_unknown || 0;
            const resetFetch = () => {
              isLoadMore.current = false;
              setSearch(null);
              setSortStack([{ key: "updated_at", dir: "desc" }]);
              setPage(1);
              setDisplayPage(1);
              setAllItems([]);
              setPendingTeamFilter(null);
              setCancelReasonFilter(null);
              setReschedReasonFilter(null);
            };
            const handleAcClick = () => {
              resetFetch();
              if (isAcActive) { setStatusFilter(null); setAcSubFilter(null); setAcCompanyFilter(null); }
              else { setStatusFilter("Active/Complete"); setAcSubFilter(null); setAcCompanyFilter(null); }
            };
            const handleSubClick = (type) => (e) => {
              e.stopPropagation();
              resetFetch();
              setAcCompanyFilter(null);
              if (acSubFilter === type) { setStatusFilter(null); setAcSubFilter(null); }
              else { setStatusFilter("Active/Complete"); setAcSubFilter(type); }
            };
            const handleCoClick = (co) => (e) => {
              e.stopPropagation();
              resetFetch();
              setAcSubFilter(null);
              if (acCompanyFilter === co) { setStatusFilter(null); setAcCompanyFilter(null); }
              else { setStatusFilter("Active/Complete"); setAcCompanyFilter(co); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                  anyAcSelected ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: acCount === 0 ? 0.5 : 1 }}
              >
                <div onClick={acCount > 0 ? handleAcClick : undefined} tabIndex={0} title="Show only Active/Complete job orders"
                  className={`cursor-pointer ${isAcActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Active/Complete</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{acCount}</span>
                  </div>
                </div>
                {/* Desktop-only: status + company breakdowns side by side */}
                <div className="hidden sm:flex items-stretch gap-0 mt-0.5 border-t border-green-300 pt-1">
                  {/* Status breakdown */}
                  <div className="flex items-center justify-center gap-1 flex-1">
                    <button onClick={handleSubClick("gofiber_only")} disabled={gfOnly === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isGfActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${gfOnly === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="GoFiber Active but JO not Completed"
                    >
                      <span className="font-semibold">{gfOnly}</span> GF
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleSubClick("jo_only")} disabled={joOnly === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isJoActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${joOnly === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="JO Completed but GoFiber not Active"
                    >
                      <span className="font-semibold">{joOnly}</span> JO
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleSubClick("both")} disabled={both === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isBothActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${both === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Both GoFiber Active and JO Completed"
                    >
                      <span className="font-semibold">{both}</span> Both
                    </button>
                  </div>
                  {/* Divider */}
                  <div className="w-px bg-green-300 mx-1 self-stretch"></div>
                  {/* Company breakdown */}
                  <div className="flex items-center justify-center gap-1 flex-1">
                    <button onClick={handleCoClick("best")} disabled={bestCo === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isBestActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${bestCo === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by BEST teams"
                    >
                      <span className="font-semibold">{bestCo}</span> BEST
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleCoClick("a6")} disabled={a6Co === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isA6Active ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${a6Co === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by A6 teams"
                    >
                      <span className="font-semibold">{a6Co}</span> A6
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleCoClick("unknown")} disabled={unkCo === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isUnkActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${unkCo === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by unidentified teams or no team"
                    >
                      <span className="font-semibold">{unkCo}</span> N/A
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Rescheduled card — enhanced with reason breakdown (desktop only) */}
          {(() => {
            const reschedCount = totalStatusCounts.Rescheduled || 0;
            const reasonCounts = totalStatusCounts.resched_reason_counts || {};
            const isMainActive = statusFilter === "Rescheduled" && !reschedReasonFilter;
            const anyActive = statusFilter === "Rescheduled";
            const resetFetch = () => {
              isLoadMore.current = false;
              setSearch(null);
              setSortStack([{ key: "updated_at", dir: "desc" }]);
              setPage(1);
              setDisplayPage(1);
              setAllItems([]);
              setPendingTeamFilter(null);
              setAcSubFilter(null);
              setAcCompanyFilter(null);
              setCancelReasonFilter(null);
            };
            const handleMainClick = () => {
              resetFetch();
              setReschedReasonFilter(null);
              if (isMainActive) setStatusFilter(null);
              else setStatusFilter("Rescheduled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              resetFetch();
              if (reschedReasonFilter === reason) { setStatusFilter(null); setReschedReasonFilter(null); }
              else { setStatusFilter("Rescheduled"); setReschedReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-blue-100 text-blue-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: reschedCount === 0 ? 0.5 : 1 }}
              >
                <div onClick={reschedCount > 0 ? handleMainClick : undefined} tabIndex={0} title="Show only Rescheduled job orders"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Rescheduled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{reschedCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-blue-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-blue-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)} disabled={count === 0}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            reschedReasonFilter === reason ? 'bg-blue-600 text-white font-bold shadow-sm' : 'hover:bg-blue-200 text-blue-900'
                          } cursor-pointer`}
                          title={`Show rescheduled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Cancelled card — enhanced with reason breakdown (desktop only) */}
          {(() => {
            const cancelCount = totalStatusCounts.Cancelled || 0;
            const reasonCounts = totalStatusCounts.cancel_reason_counts || {};
            const isMainActive = statusFilter === "Cancelled" && !cancelReasonFilter;
            const anyActive = statusFilter === "Cancelled";
            const resetFetch = () => {
              isLoadMore.current = false;
              setSearch(null);
              setSortStack([{ key: "updated_at", dir: "desc" }]);
              setPage(1);
              setDisplayPage(1);
              setAllItems([]);
              setPendingTeamFilter(null);
              setAcSubFilter(null);
              setAcCompanyFilter(null);
              setReschedReasonFilter(null);
            };
            const handleMainClick = () => {
              resetFetch();
              setCancelReasonFilter(null);
              if (isMainActive) setStatusFilter(null);
              else setStatusFilter("Cancelled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              resetFetch();
              if (cancelReasonFilter === reason) { setStatusFilter(null); setCancelReasonFilter(null); }
              else { setStatusFilter("Cancelled"); setCancelReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 transition-all hover:shadow cursor-pointer focus-within:ring-2 focus-within:ring-blue-400 ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: cancelCount === 0 ? 0.5 : 1 }}
              >
                <div onClick={cancelCount > 0 ? handleMainClick : undefined} tabIndex={0} title="Show only Cancelled job orders"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Cancelled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{cancelCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-red-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-red-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)} disabled={count === 0}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            cancelReasonFilter === reason ? 'bg-red-600 text-white font-bold shadow-sm' : 'hover:bg-red-200 text-red-900'
                          } cursor-pointer`}
                          title={`Show cancelled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>

        {/* Table — fills remaining space. Kept mounted during refetches so
            the layout stays stable and no alarming empty-state flashes. */}
        <div className="flex-1 flex flex-col min-h-0">
          <JobOrderTable
            items={filteredItems}
            sortStack={sortStack}
            onSortClick={handleSortClick}
            onRowClick={(row) => canViewDetail && setSelectedRow(row)}
            onStatusClick={statusMenuActions.length > 0 ? handleStatusClick : undefined}
            onCallStatusClick={canEditCallStatus ? handleCallStatusClick : undefined}
            onTeamClick={(canEditTeam || canEditTerminalTeam) ? handleTeamClick : undefined}
            onCompanyClick={canEditCompany ? handleCompanyClick : undefined}
            onCustomerLocationClick={canAccessNiDashboard ? (row) => {
              navigate("/ni/dashboard", {
                state: {
                  dateFrom: dateFilter.from,
                  dateTo: dateFilter.to,
                  locateSubscriber: {
                    id: row.id,
                    joNumber: row.jo_number,
                    customerName: row.customer_full_name,
                    address: row.complete_address,
                    municipality: row.municipality,
                    barangay: row.barangay,
                    accountNumber: row.account_number,
                    latitude: row.xlsx_metadata?.latitude || row.latitude || null,
                    longitude: row.xlsx_metadata?.longitude || row.longitude || null,
                    locationSaved: row.xlsx_metadata?.location_saved || row.location_saved || false,
                  },
                },
              });
            } : undefined}
            userCompanyId={user?.company_id}
            canEditCrossCompanyTeam={canEditCrossCompanyTeam}
            scrollRef={scrollRef}
            onScroll={handleTableScroll}
            loadingMore={loadingMore || loading}
            canViewChat={canViewChat}
            onChatClick={(row) => !row.is_history && setChatJobOrderId(row.id)}
            hiddenColumns={hiddenColumns}
            canEditTerminalTeam={canEditTerminalTeam}
          />
          {/* Record count */}
          <div className="flex items-center pt-2 pb-1 flex-shrink-0 text-sm text-gray-600">
            <span>
              {loading && filteredItems.length === 0
                ? "Loading…"
                : `${total} Job Order${total !== 1 ? "s" : ""}`}
            </span>
          </div>
        </div>
      </main>

      {/* Detail modal */}
      {selectedRow && (
        <JobOrderDetailModal
          jobOrderId={selectedRow.id}
          onClose={() => setSelectedRow(null)}
          onRefresh={handleRefresh}
          historyMode={selectedRow.is_history}
          initialHistoryJobDate={selectedRow.is_history ? selectedRow.job_date : null}
        />
      )}

      {/* Chat modal */}
      {chatJobOrderId && (
        <ChatModal
          jobOrderId={chatJobOrderId}
          onClose={() => setChatJobOrderId(null)}
          onCountUpdate={(joId, count) => {
            setAllItems((prev) =>
              prev.map((item) =>
                item.id === joId && !item.is_history
                  ? { ...item, chat_count: count, has_unread_chat: false, unread_count: 0 }
                  : item
              )
            );
          }}
          notifRef={chatNotifRef}
        />
      )}

      {/* Quick status-action menu */}
      {statusMenu && (
        <StatusActionMenu
          anchorRect={statusMenu.rect}
          onSelect={handleStatusAction}
          onClose={() => setStatusMenu(null)}
          isMobile={isMobile}
          visibleActions={statusMenuActions}
        />
      )}

      {/* Direct status modals from quick action */}
      {directAction?.type === "complete" && (
        <QuickCompleteDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={handleDirectActionDone}
          onCancel={() => setDirectAction(null)}
        />
      )}
      {directAction?.type === "cancel" && (
        <QuickCancelDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={handleDirectActionDone}
          onCancel={() => setDirectAction(null)}
        />
      )}
      {directAction?.type === "reschedule" && (
        <RescheduleModal
          key={directAction.jo.id}
          jobOrderId={directAction.jo.id}
          joNumber={directAction.jo.jo_number}
          currentJobDate={directAction.jo.job_date}
          ageingOriginDate={directAction.jo.ageing_origin_date}
          customerName={directAction.jo.customer_full_name}
          customerAddress={directAction.jo.complete_address}
          initialRemark={directAction.jo.jo_status_remarks || ""}
          initialTeamNumber={directAction.jo.team_number || ""}
          initialPersonnelNames={directAction.jo.personnel_names || ""}
          initialPersonnel1Id={directAction.jo.personnel_1_id || null}
          initialPersonnel2Id={directAction.jo.personnel_2_id || null}
          teamAssignedByCompany={directAction.jo.team_assigned_by_company}
          onClose={() => setDirectAction(null)}
          onComplete={() => handleDirectActionDone()}
        />
      )}

      {/* Quick call-status action menu */}
      {csMenu && (
        <CallStatusActionMenu
          options={NI_CALL_OPTIONS}
          anchorRect={csMenu.rect}
          onSelect={handleCallStatusSelect}
          onClose={() => setCsMenu(null)}
          isMobile={isMobile}
        />
      )}

      {/* Quick call-status dialog */}
      {csAction && (
        <QuickCallStatusDialog
          key={csAction.jo.id}
          jo={csAction.jo}
          selectedStatus={csAction.selectedStatus}
          onDone={handleCallStatusDone}
          onCancel={() => setCsAction(null)}
        />
      )}

      {/* Inline team assignment popover */}
      {teamMenu && (
        <TeamAssignmentPopover
          key={teamMenu.jo.is_history ? `h-${teamMenu.jo.history_id}` : teamMenu.jo.id}
          anchorRect={teamMenu.rect}
          record={teamMenu.jo}
          assignmentDate={teamMenu.jo.job_date}
          apiPath="/job-orders"
          companyId={
            canEditCrossCompanyTeam && teamMenu.jo.company_id
              ? teamMenu.jo.company_id
              : user?.company_id
          }
          onSave={handleTeamSave}
          onClose={() => setTeamMenu(null)}
          isHistory={!!teamMenu.jo.is_history}
        />
      )}

      {/* Inline company assignment popover */}
      {companyMenu && (
        <CompanyAssignmentPopover
          key={companyMenu.jo.id}
          anchorRect={companyMenu.rect}
          record={companyMenu.jo}
          onSave={handleCompanySave}
          onClose={() => setCompanyMenu(null)}
        />
      )}

    </div>
  );
}
